import React from 'react'
import { color } from '@figma/fpl-tokens/tokens.stylex'
import { t } from '@figma/i18n/translation'
import {
  authClaimInviteHeader,
  authSignInToFigma,
  authSignUpForFigma,
} from '@figma/i18n-dictionaries/en/web/auth'
import history from '@web/lib/history'
import { useAtomValue } from '@web/lib/jotai'
import stylex from '@web/lib/stylex'
import { ResourceType } from '@web/livegraph-types/enums'
import { trackDefinedEvent } from '@web/o11y/analytics'
import { TrackingContext } from '@web/o11y/analytics/tracking_context'
import { LoadTimer } from '@web/o11y/load_timer'
import { AppAuthAppType, AuthFormState } from '@web/redux/auth'
import { flashesAtom } from '@web/ui-platform/flash/atoms'
import { FlashView } from '@web/ui-platform/flash/view'

import initialOptions from 'lib/initial_options'
import { referralCodeAtom } from 'shared/auth/auth_atoms'
import { type AuthInitialOptions, referralCodeStatus } from 'shared/auth/auth_models'
import { useAuthState } from 'shared/auth/use_auth_state'
import {
  AuthView,
  useHeaderAction,
  useHeaderActionText,
} from 'shared/auth/views/auth_view/auth_view'
import { ReferralSignupMessage } from 'shared/auth/views/auth_view/authentication/sign_in_and_up/referral_signup_message'
import { brandVars } from 'shared/auth/views/auth_view/brand.stylex'
import { SimplePageAuthViewWrapper } from 'shared/auth/views/auth_view/simple_page_auth_view_wrapper'
import { SimplePage } from 'shared/components/simple_page/simple_page'
import { AuthCookieBanner } from 'shared/cookie_banners/auth_cookie_banner'

import { GoogleTagManagerIframe } from 'figma_app/lib/google_tag_manager'
import { UserDataGatekeeper } from 'figma_app/lib/user_data_gatekeeper'

interface AuthAppInitialOptions extends AuthInitialOptions {
  resource_type: ResourceType | null
}

const brandStyle = stylex.create({
  brand: {
    fontFamily: brandVars.fontFamily,
    color: color.text,
    backgroundColor: color.bg,
    fontVariationSettings: brandVars.fontVariationSettings,
    fontOpticalSizing: brandVars.fontOpticalSizing,
  },
})

const GEN_1_AUTH_ORIGINS: ReadonlyArray<string> = [
  'logged_out_footer_continue_with_google',
  'logged_out_footer',
  'request_permissions_entry',
  'google_one_tap_logged_out_file',
  'logged_out_main_menu_toolbar',
  'prototype_header_nav',
  'prototype_comment_signed_out',
  'team_invite_link',
  'claim_invite',
  'account_picker',
  'signed_out_edit',
  'saml_sso',
  'figma_rev_logged_out_footer_continue_with_google',
  'figma_rev_logged_out_header',
  'figma_rev_logged_out_header_with_google',
  'figma_rev_logged_out_footer',
  'google_one_tap_prototype',
  'prototype_header_nav_google_sso',
  'slides_presentation_header_nav_google_sso',
  'mobile_viewer_logged_out_footer',
]

function landedFromGen1Origin(origin: string | undefined): boolean {
  return (
    !!new URLSearchParams(history.location.search).get('is_not_gen_0') ||
    (!!origin && GEN_1_AUTH_ORIGINS.includes(origin))
  )
}

export function AuthApp() {
  return (
    <LoadTimer label="AuthApp">
      <_AuthApp />
    </LoadTimer>
  )
}

function _AuthApp() {
  const [formState] = useAuthState('formState')
  const [origin] = useAuthState('origin')
  const [appAuthAppType] = useAuthState('appAuthAppType')
  const headerAction = useHeaderAction(formState)
  const headerActionText = useHeaderActionText(formState)
  const flashes = useAtomValue(flashesAtom)
  const options = initialOptions<AuthAppInitialOptions>()
  const hasReferralCode = !!useAtomValue(referralCodeAtom)

  React.useEffect(() => {
    if (hasReferralCode) {
      trackDefinedEvent('referral.entry_page_shown', {})
    }
  }, [hasReferralCode])

  const referralStatus = options.referral_status
  const isShowingReferralStatus = formState === AuthFormState.SIGN_UP && !!referralStatus
  const hasTrackedReferralStatus = React.useRef(false)
  React.useEffect(() => {
    if (!isShowingReferralStatus || !referralStatus || hasTrackedReferralStatus.current) {
      return
    }
    hasTrackedReferralStatus.current = true
    trackDefinedEvent('referral.code_status_shown', {
      code_status: referralCodeStatus(referralStatus),
      credit_amount: referralStatus.credit_amount,
    })
  }, [isShowingReferralStatus, referralStatus])

  let header
  let statusMessage
  let trackingName = 'Auth app view'

  switch (formState) {
    case AuthFormState.SIGN_IN:
      header = t(authSignInToFigma)
      break
    case AuthFormState.SIGN_UP:
      header = t(authSignUpForFigma)
      if (options.referral_status) {
        statusMessage = <ReferralSignupMessage referralStatus={options.referral_status} />
      }
      break
  }

  if (landedFromGen1Origin(origin)) {
    switch (options.resource_type) {
      case ResourceType.TEAM:
        header = t(authClaimInviteHeader)
        trackingName = 'Auth app view > Team'
        break
      case ResourceType.FOLDER:
        header = t(authClaimInviteHeader)
        trackingName = 'Auth app view > Project'
        break
    }
  }

  // On FigJam for iOS, we need to hide the header because it gives the user a way to access the pricing page.
  // https://app.asana.com/0/1203051674381929/1205078200729466
  const shouldHidePageHeader = appAuthAppType === AppAuthAppType.FIGJAM_MOBILE
  return shouldHidePageHeader ? (
    <>
      <SimplePageAuthViewWrapper fullscreen={false}>
        <AuthView id="auth-view-page" header={header} statusMessage={statusMessage} />
      </SimplePageAuthViewWrapper>
      <FlashView flashes={flashes} />
      <UserDataGatekeeper>
        <GoogleTagManagerIframe />
      </UserDataGatekeeper>
      <AuthCookieBanner />
    </>
  ) : (
    <TrackingContext name={trackingName}>
      <SimplePage
        headerAction={headerAction}
        headerActionText={headerActionText}
        className={stylex.classNames(brandStyle.brand)}
      >
        <SimplePageAuthViewWrapper>
          <AuthView id="auth-view-page" header={header} statusMessage={statusMessage} />
        </SimplePageAuthViewWrapper>
        <FlashView flashes={flashes} />

        <UserDataGatekeeper>
          <GoogleTagManagerIframe />
        </UserDataGatekeeper>
        <AuthCookieBanner />
      </SimplePage>
    </TrackingContext>
  )
}
